import { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Button, ButtonLink, LoadingTable } from '../../../components/base';
import { ConfirmModal } from '../../../components/common/ConfirmModal';
import { DataTable, IDataTableColumn } from '../../../components/common/DataTable';
import { BlockCard, PageBody, PageContainer, PageHeader } from '../../../components/container';
import { BadgesRole } from '../../../components/shared/system/badges-role/BadgesRole';
import { useLookupRoles } from '../../../components/shared/system/useLookupRoles';
import { BaseShowDetail } from '../../../context/base/BaseShowDetail';
import { eBaseActionStatus } from '../../../context/base/eBaseActionStatus';
import { MenuItemResponse } from '../../../context/models/menus/MenuItemResponse';
import {
	MenuRemoveRoleRequest,
	MenuUpdateRoleMultipleRequest,
	MenuUpdateRoleRequest,
} from '../../../context/models/menus/MenuUpdateRoleRequest';
import { MenusActionTypes } from '../../../context/stores/menus/IMenuActionTypes';
import { actions } from '../../../context/stores/rootActions';
import { AppState } from '../../../context/stores/rootReducers';
import MenuPhanQuyenMultiModal from './phan-quyen-multi/MenuPhanQuyenMultiModal';
import MenuPhanQuyenModal from './phan-quyen/MenuPhanQuyenModal';

type IMenusPageProps = {
	titlePage: string;
	status: eBaseActionStatus;
	menus_all: MenuItemResponse[];
	selectedIds: number[];
	showChangeRole: BaseShowDetail<MenuItemResponse>;
	showChangeRoleMulti: boolean;
	showConfirm: boolean;
	handleReloadData: () => void;
	handleChangRole: (data: MenuUpdateRoleRequest) => void;
	handleChangRoleMultiple: (data: MenuUpdateRoleMultipleRequest) => void;
	handleRemoveRole: (data: MenuRemoveRoleRequest) => void;
	onChangeSelectedIds: (ids: number[]) => void;
	onShowChangeRole: (data: BaseShowDetail<MenuItemResponse>) => void;
	onShowChangeRoleMulti: (isShow: boolean) => void;
	onShowConfirm: (isShow: boolean) => void;
};

const mapStateToProps = (state: AppState) => {
	return {
		titlePage: `Phân quyền chức năng`,
		status: state.menus.status,
		menus_all: state.menus.menus_all,
		showChangeRole: state.menus.showChangeRole,
		showChangeRoleMulti: state.menus.showChangeRoleMulti,
		selectedIds: state.menus.selectedIds,
		showConfirm: state.menus.showConfirm,
		allRoles: state.roles.allRoles,
	};
};

const mapDispatchToProps = (dispatch: Dispatch<MenusActionTypes>) => ({
	handleReloadData: () => {
		dispatch(actions.menus.getAllRequest());
	},
	handleChangRole: (data: MenuUpdateRoleRequest) => {
		dispatch(actions.menus.changeRoleRequest(data));
	},
	handleChangRoleMultiple: (data: MenuUpdateRoleMultipleRequest) => {
		dispatch(actions.menus.changeRoleMultiRequest(data));
	},
	handleRemoveRole: (data: MenuRemoveRoleRequest) => {
		dispatch(actions.menus.removeRoleRequest(data));
	},
	onChangeSelectedIds: (ids: number[]) => {
		dispatch(actions.menus.changeSelectedIds(ids));
	},
	onShowChangeRole: (data: BaseShowDetail<MenuItemResponse>) => {
		dispatch(actions.menus.showChangeRole(data));
	},
	onShowChangeRoleMulti: (isShow: boolean) => {
		dispatch(actions.menus.showChangeRoleMulti(isShow));
	},
	onShowConfirm: (isShow: boolean) => {
		dispatch(actions.menus.showConfirm(isShow));
	},
});

const MenuPage = (props: IMenusPageProps) => {
	const { roles_lookup } = useLookupRoles();
	const {
		status,
		menus_all,
		selectedIds,
		showChangeRole,
		showChangeRoleMulti,
		showConfirm,
		titlePage,
		handleChangRole,
		handleChangRoleMultiple,
		handleReloadData,
		handleRemoveRole,
		onChangeSelectedIds,
		onShowChangeRole,
		onShowChangeRoleMulti,
		onShowConfirm,
	} = props;

	useEffect(() => {
		handleReloadData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (status === eBaseActionStatus.reload) handleReloadData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	const calculateFilterExpression = (filterValue: any, selectedFilterOperation: any, target: any) => {
		if (target === 'search' && typeof filterValue === 'string') {
			return [target.dataField, 'contains', filterValue];
		}
		return function (data: any) {
			const list = data.roleids || [];
			return list.indexOf(filterValue) !== -1;
		};
	};

	const columns = useMemo((): IDataTableColumn<MenuItemResponse>[] => {
		return [
			{ accessorKey: 'sortOrder', header: 'TT', size: 80, meta: { align: 'center' } },
			{
				accessorKey: 'icon',
				header: 'Icon',
				size: 80,
				meta: { align: 'center' },
				cell: ({ getValue }) => <em className={`icon ni ni-${getValue() as string || 'menu-squared'} text-lg`}></em>
			},
			{ accessorKey: 'name', header: 'Chức năng', minSize: 150 },
			{
				accessorKey: 'parentId',
				header: 'Chức năng cha',
				size: 160,
				cell: ({ getValue }) => {
					const val = getValue() as number;
					const parent = menus_all.find((m) => m.id === val);
					return <span className='font-medium'>{parent ? parent.name : (val > 0 ? val : '-')}</span>;
				}
			},
			{
				accessorKey: 'roleIds',
				header: 'Phân quyền',
				size: 240,
				cell: ({ getValue }) => {
					const val = (getValue() as number[]) || [];
					return <BadgesRole roles_all={roles_lookup} value={val} />;
				}
			},
			{
				accessorKey: 'path',
				header: 'Đường dẫn',
				size: 250,
				cell: ({ getValue }) => <code className='text-xs bg-muted px-1.5 py-0.5 rounded'>{getValue() as string}</code>
			},
			{
				id: 'actions',
				header: 'Thao tác',
				meta: { align: 'center' },
				size: 80,
				cell: ({ row }) => row.original.id > 0 ? (
					<ButtonLink
						onClick={() => onShowChangeRole({ isShow: true, detailData: row.original })}
						title='Phân quyền'
						icon='ni ni-shield-check-fill'
						theme='link'
					/>
				) : null
			}
		];
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [menus_all, roles_lookup]);

	return (
		<>
			<PageContainer>
				<PageHeader title={titlePage}>
					<div className='flex flex-wrap items-center justify-end'>
						<ul className='flex flex-wrap items-center gap-2 [&>li]:list-none'>
							<li>
								<Button
									text={`Hủy phân quyền (${selectedIds.length})`}
									icon='icon ni ni-trash'
									className='hidden md:inline-flex'
									theme='danger'
									isDisabled={selectedIds.length <= 0}
									isLoading={status === eBaseActionStatus.saving}
									onClick={() => onShowConfirm(true)}
								/>
							</li>
							<li>
								<Button
									text={`Thay đổi phân quyền (${selectedIds.length})`}
									icon='icon ni ni-plus'
									theme='primary'
									className='hidden md:inline-flex'
									isDisabled={selectedIds.length <= 0}
									isLoading={status === eBaseActionStatus.saving}
									onClick={() => onShowChangeRoleMulti(true)}
								/>
							</li>
						</ul>
					</div>
				</PageHeader>
				<PageBody>
					<BlockCard>
						{status === eBaseActionStatus.loading && <LoadingTable />}
						{status !== eBaseActionStatus.loading && (
							<DataTable
								data={menus_all}
								columns={columns}
								isLoading={status !== eBaseActionStatus.complete}
								enableRowSelection
								rowSelection={selectedIds.reduce((acc, id) => ({ ...acc, [String(id)]: true }), {})}
								onRowSelectionChange={(selection) => {
									onChangeSelectedIds(Object.keys(selection).filter(k => selection[k]).map(Number));
								}}
								enablePagination
								totalRecords={menus_all.length}
								enableGlobalFilter
								toolbar={{ isShowSearch: true, searchPlaceholder: `Tìm kiếm chức năng...` }}
								onRefresh={handleReloadData}
							/>
						)}
					</BlockCard>
				</PageBody>
			</PageContainer>
			{showChangeRole && showChangeRole.isShow && showChangeRole.detailData && (
				<>
					<MenuPhanQuyenModal
						isSaving={status === eBaseActionStatus.saving}
						isShow={showChangeRole.isShow}
						menuModel={showChangeRole.detailData}
						onClose={() => onShowChangeRole({ isShow: false })}
						onSubmit={(data: MenuUpdateRoleRequest) => handleChangRole(data)}
						title={`Phân quyền chức năng`}
					/>
				</>
			)}
			{showChangeRoleMulti && selectedIds.length > 0 && (
				<>
					<MenuPhanQuyenMultiModal
						isSaving={status === eBaseActionStatus.saving}
						isShow={showChangeRoleMulti}
						menu_ids={selectedIds}
						onClose={() => onShowChangeRoleMulti(false)}
						onSubmit={(data: MenuUpdateRoleMultipleRequest) => handleChangRoleMultiple(data)}
						title={`Phân quyền nhiều chức năng`}
					/>
				</>
			)}
			{showConfirm && (
				<>
					<ConfirmModal
						show={showConfirm}
						innerText={`Bạn chắc chắn muốn hủy phân quyền ${selectedIds.length} chức năng đã chọn ?`}
						type='danger'
						onClose={() => onShowConfirm(false)}
						onConfirmed={() => handleRemoveRole({ menu_ids: selectedIds })}
						isLoading={status === eBaseActionStatus.loading || status === eBaseActionStatus.saving}
						btnConfirmText='Hủy phân quyền'
						btnCloseText='Đóng'
					/>
				</>
			)}
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(MenuPage);
